<template>
  <div
    class="base-radar-view"
    :style="{ width: canvasSize.width + 'px', height: canvasSize.height + 'px' }"
    ref="rootRef"
  >
    <canvas ref="canvasRef" :width="canvasSize.width" :height="canvasSize.height"></canvas>

    <div class="overlay-layer">
      <slot name="targets">
        <!-- <div
          v-for="target in targets"
          :key="target.id"
          class="target-point"
          :style="getTargetStyle(target)"
        >
          <span class="target-id">{{ target.id }}</span>
        </div> -->
        <div
          v-for="target in targets"
          :key="target.id"
          class="target-point"
          :style="getTargetStyle(target)"
        >
          <div class="target-box">
            <img :width="32" :height="32" src="./person.png" />
            <span class="target-id">{{ target.id }}</span>
          </div>
        </div>
      </slot>

      <div
        class="editable-furniture-layer"
        :style="{
          overflow: showPanel ? 'visible' : 'hidden',
        }"
      >
        <slot name="furniture">
          <baseRadarEditableFurniture
            :angle="angle"
            :coordinates="coordinates"
            :canvas-size="500"
            :disabled="!showPanel"
          />
        </slot>
      </div>

      <slot name="subregion">
        <baseRadarEditableSubregion
          v-if="showPanel && toolbarTabKey === 2"
          :angle="angle"
          :canvas-size="500"
          :disabled="!showPanel"
          :has-bed="roomStore.localFurnitureItems.some((item) => item.type === 'bed')"
        />
      </slot>

      <slot></slot>
    </div>

    <div
      v-if="showDetectArea"
      class="detect-area"
      :style="{
        position: 'absolute',
        left: 0 + 'px',
        top: 0 + 'px',
        width: 500 + 'px',
        height: 500 + 'px',
      }"
      ref="containerRef"
    >
      <div class="detect-area-text">
        <div
          v-if="isEffectArea"
          class="block-item"
          :style="getBlockStyle(detectBlock)"
          @mousedown.self="startDrag(detectBlock, $event)"
        >
          <div
            class="resize-handle"
            :style="{ backgroundColor: '#ffafaf' }"
            @mousedown.stop="startResize(detectBlock, $event)"
          >
          </div>
        </div>
      </div>
    </div>

    <template v-if="mode === 'edit'">
      <baseRadarToolPanel
        v-model:show="showPanel"
        v-model:tabKey="toolbarTabKey"
        :sleep-arr="props.sleepArr"
        :client-id="props.clientId"
        @success="handleSuccess"
        @reloadAlarm="handleReloadAlarm"
      />
      <div class="setting-btn" @click="toggleSettingPanel" v-permission="'room:save'">
        房间配置 {{ showPanel ? '|<' : '>|' }}
      </div>

      <div v-if="showDetectArea" class="inout-panel">
        <div class="header">
          <div>进出检测区域</div>
          <a-space>
            <a-button size="small" @click="resetDetectArea">重置</a-button>
            <a-button type="primary" size="small" @click="saveDetectArea" :disabled="!isEffectArea"
              >保存</a-button
            >
          </a-space>
        </div>

        <div class="container">
          <div>
            <template v-if="isEffectArea"> 检测区域：{{ detectAreaSize }} </template>
            <template v-else>
              暂无进出区域，立即
              <a-button size="small" type="link" @click="handleAddArea">添加</a-button>
            </template>
          </div>
          <div class="config-tip">
            <div class="title">配置提示：</div>
            <div class="item"> 1. 进出区域，请配置为长方形。 </div>
            <div class="item"> 2. 进出区域，请保持在蓝色检测区域内，不要贴边。</div>
          </div>
        </div>
      </div>
      <div class="inout-btn" @click="toggleInoutPanel" v-permission="'alarm:save'">
        进出配置 {{ showDetectArea ? '|<' : '>|' }}
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onMounted, provide, ref, watch, type CSSProperties, computed } from 'vue'
import radarUrl from '@/assets/furnitures/radar.png'
import { convert_region_r2c, rotateRect_cw } from '@/utils/coordTransform'
import type { TargetPoint } from '@/types/radar'
import { useRoomStore } from '@/stores/room'
import { useAlarmStore } from '@/stores/alarm'
import * as alarmApi from '@/api/alarm'
import { message } from 'ant-design-vue'
import type { LocalSubRegionItem } from '@/api/room/types'

defineOptions({ name: 'BaseRadarView' })
const roomStore = useRoomStore()
const alarmStore = useAlarmStore()

interface CanvasSize {
  width: number
  height: number
}

interface DetectBlock extends LocalSubRegionItem {
  id: string
  name: string
  data: {
    id: string
    name: string
    region: string
    param: string
  }
}

interface Props {
  roomId?: string // 房间 ID
  devId?: string // 设备 ID
  clientId?: string // 设备MAC
  coordinates: number[] // 检测区域坐标
  angle?: number // 旋转角度，默认 0 度
  targets?: TargetPoint[] // 目标点数组
  canvasSize?: CanvasSize // 画布尺寸，默认 500x500
  mode?: 'view' | 'edit'
  inoutArr?: DetectBlock[]
  sleepArr?: DetectBlock[]
}

const props = withDefaults(defineProps<Props>(), {
  roomId: '',
  devId: '',
  clientId: '',
  canvasSize: () => ({
    width: 500,
    height: 500,
  }),
  angle: 0,
  mode: 'view',
  inoutArr: () => [],
  sleepArr: () => [],
})

const emit = defineEmits(['success', 'reloadAlarm', 'resetAlarm'])

provide('roomId', props.roomId)
provide('devId', props.devId)

const canvasRef = ref<HTMLCanvasElement | null>(null)
let ctx: CanvasRenderingContext2D | null = null

/**
 * 网格选项
 * 用于绘制背景网格的选项
 * @param width 画布宽度
 * @param height 画布高度
 * @param spacing 网格间距，默认 50
 * @param color 网格线颜色，默认 '#d9d9d7'
 * @param lineWidth 网格线宽度，默认 0.5
 */
interface GridOptions {
  width: number
  height: number
  spacing?: number
  color?: string
  lineWidth?: number
}
/**
 * 绘制背景网格
 * 50px间距的灰色网格线，辅助定位
 * @param ctx 画布上下文
 * @param options 网格选项
 */
const drawGrid = (ctx: CanvasRenderingContext2D, options: GridOptions) => {
  const { width, height, spacing = 50, color = '#d9d9d7', lineWidth = 0.5 } = options

  if (!ctx) return

  ctx.strokeStyle = color
  ctx.lineWidth = lineWidth

  // 水平网格线
  for (let y = spacing; y < height; y += spacing) {
    ctx.beginPath()
    ctx.moveTo(0, y)
    ctx.lineTo(width, y)
    ctx.stroke()
  }

  // 垂直网格线
  for (let x = spacing; x < width; x += spacing) {
    ctx.beginPath()
    ctx.moveTo(x, 0)
    ctx.lineTo(x, height)
    ctx.stroke()
  }
}

/**
 * 坐标轴选项
 * 用于绘制坐标轴的选项
 * @param width 画布宽度
 * @param height 画布高度
 * @param angle 旋转角度（单位：度），默认 0
 * @param xColor X轴颜色，默认 'red'
 * @param yColor Y轴颜色，默认 'green'
 */
interface CoordinateOptions {
  width: number
  height: number
  angle?: number
  xColor?: string
  yColor?: string
}
/**
 * 绘制坐标轴
 * 虚线样式的X轴和Y轴，标识坐标系中心
 * @param ctx 画布上下文
 * @param options 坐标轴选项
 */
const drawCoordinateAxes = (ctx: CanvasRenderingContext2D, options: CoordinateOptions) => {
  const { width, height, angle = 0, xColor = '#2c3e50', yColor = '#2c3e50' } = options

  if (!ctx) return

  const centerX = width / 2
  const centerY = height / 2

  // 只接受 0, 90, 180, 270，其他归为 0
  const validAngles: number[] = [0, 90, 180, 270]
  const rotationDeg: number = validAngles.includes(angle) ? angle : 0
  const rotationRad = (rotationDeg * Math.PI) / 180

  ctx.save()
  ctx.translate(centerX, centerY)
  ctx.rotate(rotationRad)
  ctx.translate(-centerX, -centerY)

  ctx.lineWidth = 1
  ctx.setLineDash([5, 5])

  // 绘制X轴（水平线）
  ctx.strokeStyle = xColor
  ctx.beginPath()
  ctx.moveTo(0, centerY)
  ctx.lineTo(width, centerY)
  ctx.stroke()

  // 绘制Y轴（垂直线）
  ctx.strokeStyle = yColor
  ctx.beginPath()
  ctx.moveTo(centerX, 0)
  ctx.lineTo(centerX, height)
  ctx.stroke()

  ctx.setLineDash([])
  ctx.restore()
}

/**
 * 雷达图图片选项
 * 用于绘制雷达图图片的选项
 * @param width 画布宽度
 * @param height 画布高度
 * @param radarImage 雷达图图片
 * @param angle 旋转角度，默认 0
 * @param size 图片尺寸（宽高），默认 20
 * @param offset 图片偏移角度，默认 90 度 指向X轴
 */
interface RadarImageOptions {
  width: number
  height: number
  radarImage: HTMLImageElement
  angle?: number
  size?: number
  offset?: number
}
/**
 * 绘制雷达图
 * 图片以指定角度旋转，默认指向X轴
 * @param ctx 画布上下文
 * @param options 雷达图图片选项
 */
const drawRadarImage = (ctx: CanvasRenderingContext2D, options: RadarImageOptions) => {
  const { width, height, angle = 0, size = 20, radarImage, offset = 90 } = options

  const centerX = width / 2
  const centerY = height / 2
  const halfSize = size / 2
  const angleRad = ((angle + offset) * Math.PI) / 180

  ctx.save()
  ctx.translate(centerX, centerY)
  ctx.rotate(angleRad)
  ctx.drawImage(radarImage, -halfSize, -halfSize, size, size)
  ctx.restore()
}

/**
 * 检测区域选项
 * 用于绘制检测区域的选项
 * @param area 检测区域坐标 [xStart, xEnd, yStart, yEnd]
 * @param radarPosition 雷达图位置 { x_radar, y_radar }
 * @param angle 旋转角度（单位：度），默认 0
 */
interface DetectionAreaOptions {
  area: number[] // 检测区域坐标 [xStart, xEnd, yStart, yEnd]
  radarPosition: {
    x_radar: number
    y_radar: number
  }
  angle?: number
}

const detectArea = ref<{
  left: number
  top: number
  width: number
  height: number
}>({
  left: 0,
  top: 0,
  width: 0,
  height: 0,
})

const detectBlock = ref({
  startXx: 0,
  stopXx: 50,
  startYy: 0,
  stopYy: 100,
  startZz: 0,
  stopZz: 0,
  pixelX: 0,
  pixelY: 0,
  pixelWidth: 50,
  pixelHeight: 100,
  // 接口需要保存的数据
  name: '双绊线检测',
  eventVal: 101,
  alarmTimePlan: {
    startDate: '2025-01-01',
    stopDate: '2099-12-31',
    timeRange: JSON.stringify([{ start_time: '00:00:00', end_time: '23:59:59' }]),
    monthDays: JSON.stringify([]),
    weekdays: JSON.stringify([1, 2, 3, 4, 5, 6, 7]),
  },
  linkagePushWechatService: 0,
  enable: 1,
})

const getBlockStyle = (block: LocalSubRegionItem): CSSProperties => ({
  left: `${block.pixelX}px`,
  top: `${block.pixelY}px`,
  width: `${block.pixelWidth}px`,
  height: `${block.pixelHeight}px`,
  border: `2px solid ${block.isActive ? 'yellow' : '#ffafaf'}`,
  position: 'absolute',
  cursor: 'move',
  backgroundColor: block.isBed ? 'rgba(26,188,26,0.1)' : 'rgba(24,144,255,0.1)',
  zIndex: 1000,
})

const containerRef = ref<HTMLElement>()
const getRect = () =>
  containerRef.value?.getBoundingClientRect() || { left: 0, top: 0, width: 0, height: 0 }

const startDrag = (block: LocalSubRegionItem, e: MouseEvent) => {
  if (!isEffectArea.value) return
  e.stopPropagation()
  block.isActive = true

  const rect = getRect()
  const offsetX = e.clientX - rect.left - block.pixelX
  const offsetY = e.clientY - rect.top - block.pixelY

  const move = (e: MouseEvent) => {
    const newX = e.clientX - rect.left - offsetX
    const newY = e.clientY - rect.top - offsetY
    block.pixelX = Math.max(0, Math.min(newX, rect.width - block.pixelWidth))
    block.pixelY = Math.max(0, Math.min(newY, rect.height - block.pixelHeight))

    // 实时更新雷达坐标
    const C2R = alarmStore.handlerRegionC2R(block, props.angle)
    block.startXx = C2R.startXx
    block.stopXx = C2R.stopXx
    block.startYy = C2R.startYy
    block.stopYy = C2R.stopYy
  }

  const up = () => {
    block.isActive = false
    document.removeEventListener('mousemove', move)
    document.removeEventListener('mouseup', up)
  }

  document.addEventListener('mousemove', move)
  document.addEventListener('mouseup', up)
}

const startResize = (block: LocalSubRegionItem, e: MouseEvent) => {
  if (!isEffectArea.value) return
  e.stopPropagation()
  e.preventDefault()

  block.isResizing = true
  const startX = e.clientX
  const startY = e.clientY
  const initW = block.pixelWidth
  const initH = block.pixelHeight
  const rect = getRect()

  const move = (e: MouseEvent) => {
    const deltaX = e.clientX - startX
    const deltaY = e.clientY - startY
    block.pixelWidth = Math.max(20, Math.min(initW + deltaX, rect.width - block.pixelX))
    block.pixelHeight = Math.max(20, Math.min(initH + deltaY, rect.height - block.pixelY))

    // 实时更新雷达坐标
    const C2R = alarmStore.handlerRegionC2R(block, props.angle)
    block.startXx = C2R.startXx
    block.stopXx = C2R.stopXx
    block.startYy = C2R.startYy
    block.stopYy = C2R.stopYy
  }

  const up = () => {
    block.isResizing = false
    document.removeEventListener('mousemove', move)
    document.removeEventListener('mouseup', up)
  }

  document.addEventListener('mousemove', move)
  document.addEventListener('mouseup', up)
}

/**
 * 保存检测区域
 */
/**
 * 保存检测区域
 * region 格式：[startXx, stopYy, width, height]
 * 保存雷达坐标和从雷达坐标计算的宽高
 */
const saveDetectArea = async () => {
  // 从雷达坐标计算宽高
  const width = (detectBlock.value.stopXx ?? 0) - (detectBlock.value.startXx ?? 0)
  const height = (detectBlock.value.stopYy ?? 0) - (detectBlock.value.startYy ?? 0)

  if (width === height) {
    message.error('检测区域，请配置为长方形')
    return
  }

  const params = {
    alarmPlanId: props?.inoutArr[0]?.id || undefined,
    clientId: props.clientId,
    region: JSON.stringify([
      detectBlock.value.startXx ?? 0,
      detectBlock.value.stopYy ?? 0,
      width, // 从雷达坐标计算的宽度
      height, // 从雷达坐标计算的高度
    ]),
    name: detectBlock.value.name + '_' + props.clientId,
    eventVal: detectBlock.value.eventVal,
    alarmTimePlan: detectBlock.value.alarmTimePlan,
    linkagePushWechatService: detectBlock.value.linkagePushWechatService as SwitchType,
    enable: detectBlock.value.enable as SwitchType,
    param: '',
  }
  console.log('saveDetectArea', params)
  try {
    const res = await alarmApi.saveAlarmPlan(params)
    console.log('保存检测区域成功', res)
    showDetectArea.value = false
    message.success('保存成功')
    emit('reloadAlarm')
  } catch (error) {
    console.error('保存检测区域失败', error)
  }
}

/**
 * 重置检测区域
 */
const resetDetectArea = () => {
  emit('resetAlarm')
  showDetectArea.value = false
}

/**
 * 回显检测区域
 * region 格式：[startXx, stopYy, width, height]
 * 保存的是雷达坐标和从雷达坐标计算的宽高
 * 回显时恢复雷达坐标，然后使用当前角度转换到画布坐标
 */
const echoDetectArea = () => {
  console.log('🚀🚀🚀echoDetectArea', props?.inoutArr[0]?.data?.region)
  const region = JSON.parse(props?.inoutArr[0]?.data?.region || '[]')
  const startXx = region?.[0]
  const stopYy = region?.[1]
  const width = region?.[2]
  const height = region?.[3]

  // 根据保存的雷达坐标和宽高恢复完整雷达坐标
  detectBlock.value.startXx = startXx
  detectBlock.value.stopXx = startXx + width
  detectBlock.value.startYy = stopYy - height
  detectBlock.value.stopYy = stopYy

  // 使用当前角度转换到画布坐标，保证与 Canvas 绘制位置对齐
  const result = alarmStore.handlerRegionR2C(detectBlock.value, props.angle)
  detectBlock.value.pixelX = result.pixelX
  detectBlock.value.pixelY = result.pixelY
  detectBlock.value.pixelWidth = result.pixelWidth
  detectBlock.value.pixelHeight = result.pixelHeight
}
watch(() => props.inoutArr, echoDetectArea, { deep: true })

const detectAreaSize = computed(() => {
  // 从雷达坐标计算宽高，数据完全自洽
  // 这样保存和回显的数据完全一致，不受设备角度影响
  const width = (detectBlock.value.stopXx ?? 0) - (detectBlock.value.startXx ?? 0)
  const height = (detectBlock.value.stopYy ?? 0) - (detectBlock.value.startYy ?? 0)
  return [detectBlock.value.startXx, detectBlock.value.stopYy, width, height]
})

const isEffectArea = computed(() => detectAreaSize.value.every((item) => typeof item === 'number'))

/**
 * 绘制检测区域
 * @param ctx 画布上下文
 * @param options 检测区域选项
 */
const drawDetectionArea = (ctx: CanvasRenderingContext2D, options: DetectionAreaOptions) => {
  const { area, radarPosition, angle = 0 } = options
  const [xStart, xEnd, yStart, yEnd] = area
  const canvasRect = convert_region_r2c(
    {
      x_cm_start: xStart,
      x_cm_stop: xEnd,
      y_cm_start: yStart,
      y_cm_stop: yEnd,
    },
    radarPosition
  )

  const rotatedRect = rotateRect_cw(
    {
      left: canvasRect.left,
      top: canvasRect.top,
      width: canvasRect.width,
      height: canvasRect.height,
    },
    { x: radarPosition.x_radar, y: radarPosition.y_radar },
    angle
  )

  if (!ctx) return

  // 绘制蓝色边框
  ctx.strokeStyle = '#1677ff'
  ctx.lineWidth = 1
  ctx.strokeRect(rotatedRect.left, rotatedRect.top, rotatedRect.width, rotatedRect.height)

  // 添加半透明蓝色填充
  ctx.fillStyle = 'rgba(52, 152, 219, 0.3)'
  ctx.fillRect(rotatedRect.left, rotatedRect.top, rotatedRect.width, rotatedRect.height)
  detectArea.value = rotatedRect
}

/**
 * 绘制信息文本
 * 在画布左下角显示检测区域坐标信息
 */
const drawInfoText = () => {
  if (!ctx) return

  const [xStart, xEnd, yStart, yEnd] = props.coordinates

  ctx.fillStyle = '#2c3e50'
  ctx.font = '12px Arial'
  ctx.textAlign = 'left'

  const baseX = 5
  const baseY = 500 - 10

  ctx.fillText(
    `检测区域: [${xStart}, ${xEnd}, ${yStart}, ${yEnd}] 角度：${props.angle}°`,
    baseX,
    baseY
  )
}

/**
 * 绘制画布
 */
const drawCanvans = () => {
  /* 初始化画布 */
  if (!canvasRef.value) return
  ctx = canvasRef.value.getContext('2d')
  if (!ctx) return

  const CANVAS_W = props.canvasSize.width
  const CANVAS_H = props.canvasSize.height

  const CANVAS_SIZE = {
    width: CANVAS_W,
    height: CANVAS_H,
  }

  const radarPosition = {
    x_radar: CANVAS_W / 2,
    y_radar: CANVAS_H / 2,
  }

  /* 清除画布并设置背景色 */
  ctx.clearRect(0, 0, CANVAS_W, CANVAS_H)
  ctx.fillStyle = '#f2f2f0'
  ctx.fillRect(0, 0, CANVAS_W, CANVAS_H)

  /* 绘制网格和坐标轴 */
  drawGrid(ctx, { width: CANVAS_W, height: CANVAS_H })
  drawCoordinateAxes(ctx, { ...CANVAS_SIZE, angle: props.angle })

  /* 绘制雷达图 */
  const radarImage = new Image()
  radarImage.src = radarUrl
  radarImage.onload = () => {
    if (!ctx) return
    drawRadarImage(ctx, { ...CANVAS_SIZE, radarImage, angle: props.angle })
  }

  /* 绘制检测区域 */
  drawDetectionArea(ctx, { area: props.coordinates, radarPosition, angle: props.angle })

  /* 绘制信息文本 */
  drawInfoText()
}

// 监听角度变化，重新绘制画布
watch(
  () => [props.coordinates, props.angle],
  (newVal, oldVal) => {
    // 角度和区域没变化时，不重新绘制
    if (
      newVal &&
      oldVal &&
      newVal[0].toString() === oldVal[0].toString() &&
      newVal[1] === oldVal[1]
    )
      return
    drawCanvans()
  },
  {
    immediate: true,
  }
)

onMounted(() => {
  drawCanvans()
})

/**
 * 获取目标点样式
 * @param target 目标点
 * @returns 目标点样式
 */
const getTargetStyle = (target: TargetPoint) => {
  // const colorPalette = ['red']

  // const colorIndex = target.id < colorPalette.length ? target.id : colorPalette.length - 1
  // const color = colorPalette[colorIndex]

  return {
    position: 'absolute',
    width: '32px',
    height: '32px',
    // background: color,
    borderRadius: '50%',
    // left: `${target.displayX}px`,
    // top: `${target.displayY}px`,
    transform: `translate3d(${target.displayX}px, ${target.displayY}px, 0) translate(-50%, -50%)`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '12px',
    fontWeight: '600',
    pointerEvents: 'none',
    zIndex: 1000,
    transition: 'transform 1s linear',
    willChange: 'transform',
    // boxShadow: '0 0 8px rgba(0, 0, 0, 0.5)',
    userSelect: 'none',
  } as CSSProperties
}

const rootRef = ref<HTMLElement | null>(null)
const showPanel = ref(false)
const toolbarTabKey = ref<1 | 2 | 3>(1)

const toggleSettingPanel = () => {
  showDetectArea.value = false
  showPanel.value = !showPanel.value
}

const showDetectArea = ref(false)
const toggleInoutPanel = () => {
  showPanel.value = false
  showDetectArea.value = !showDetectArea.value
  console.log(detectArea.value)
  echoDetectArea()
}

const handleSuccess = () => {
  console.log('保存成功')
  emit('success')
  emit('reloadAlarm')
}

const handleAddArea = () => {
  console.log('handleAddArea ', props)
  detectBlock.value.startXx = 0
  detectBlock.value.stopXx = 50
  detectBlock.value.startYy = 0
  detectBlock.value.stopYy = 100
  saveDetectArea()
  emit('reloadAlarm')
}

const handleReloadAlarm = (upload: boolean) => {
  showPanel.value = upload
  emit('reloadAlarm')
}
</script>

<style scoped lang="less">
.base-radar-view {
  position: relative;
  display: flex;

  canvas {
    border: 2px solid #ddd;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    pointer-events: none;
  }

  .overlay-layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: auto;

    .target-point {
      position: absolute;
      pointer-events: none;
    }

    .target-box {
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;

      .target-id {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        font-size: 12px;
        color: red;
        pointer-events: none;
      }
    }

    .editable-furniture-layer {
      width: 100%;
      height: 100%;
      position: absolute;
    }
  }

  .setting-btn {
    position: absolute;
    top: 8px;
    right: 10px;
    padding: 6px 8px;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    border-radius: 4px;
    cursor: pointer;
    user-select: none;
    z-index: 999;
    opacity: 0.4;

    &:hover {
      opacity: 1;
    }
  }

  .inout-panel {
    width: 300px;
    min-height: 500px;
    background-color: #fefefe;
    border-radius: 10px;
    padding: 12px;
    position: relative;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    position: absolute;
    right: -320px;
    z-index: 999;

    .header {
      display: flex;
      justify-content: space-between;
      font-size: 16px;
      font-weight: 600;
    }

    .container {
      margin-top: 12px;

      .config-tip {
        margin-top: 12px;
        line-height: 20px;
        color: #888;
        .title {
          font-size: 14px;
          font-weight: 600;
        }
        .item {
          font-size: 12px;
          color: #aaa;
        }
      }
    }
  }

  .inout-btn {
    position: absolute;
    top: 40px;
    right: 10px;
    padding: 6px 8px;
    background: rgba(0, 0, 0, 0.6);
    color: #fff;
    border-radius: 4px;
    cursor: pointer;
    user-select: none;
    z-index: 999;
    opacity: 0.4;

    &:hover {
      opacity: 1;
    }
  }
}

.block-item {
  background: rgba(24, 144, 255, 0.1);
  pointer-events: all;
  z-index: 1;

  &:hover {
    background: rgba(24, 144, 255, 0.2);
  }

  .resize-handle {
    position: absolute;
    right: -4px;
    bottom: -4px;
    width: 15px;
    height: 15px;
    background: #1890ff;
    cursor: nwse-resize;
    font-size: 10px;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 2px;
    z-index: 3;
  }
}
</style>
